import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Send, AlertTriangle, Loader2, Check, Trash2, ExternalLink, Sparkles, Copy } from "lucide-react";
import { Button } from "./components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./components/ui/dialog";
import { Textarea } from "./components/ui/textarea";
import { Input } from "./components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_CTA = "Get the system from this video:";

/**
 * Per-row "Inject link" button. Lives next to the Copy / Short / Pencil cluster.
 *
 * Props
 *  - videoId       : YouTube video id (required)
 *  - videoTitle    : for the dialog header
 *  - ytStatus      : object from /api/youtube/oauth/status — { connected, can_write, ... }
 *  - onReauthorize : () => void — opens the OAuth popup (provided by parent)
 *  - testid        : unique data-testid prefix
 */
export function InjectLinkButton({ videoId, videoTitle, ytStatus, onReauthorize, testid }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);            // { is_injected, short_url, cta_line, injected_at }
  const [statusLoading, setStatusLoading] = useState(false);
  const [cta, setCta] = useState(DEFAULT_CTA);
  const [preview, setPreview] = useState(null);          // { previous_description, new_description, short_url }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);

  const canWrite = !!(ytStatus && ytStatus.connected && ytStatus.can_write);
  // Legacy connections made before we upgraded to `youtube.force-ssl` still
  // carry a read-only token. Those users get a graceful copy-paste fallback
  // (same dialog, same CTA) with a "reconnect to enable one-click push" nudge
  // in the banner. Detected via ytStatus.can_write.
  const writeFallbackMode = !canWrite && !!ytStatus?.connected;

  const fetchStatus = useCallback(async () => {
    if (!videoId) return;
    setStatusLoading(true);
    try {
      const { data } = await axios.get(`${API}/videos/${videoId}/inject-status`);
      setStatus(data);
      if (data.cta_line) setCta(data.cta_line);
    } catch (err) {
      // Non-fatal — status is informational
      console.warn("inject-status fetch failed:", err?.response?.data || err.message);
    } finally {
      setStatusLoading(false);
    }
  }, [videoId]);

  // Fetch once when the dialog opens so we don't burn requests on every row mount
  useEffect(() => { if (open) fetchStatus(); }, [open, fetchStatus]);

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreview(null);
    try {
      const { data } = await axios.post(`${API}/videos/${videoId}/inject-link`, {
        cta_line: cta || DEFAULT_CTA,
        dry_run: true,
      });
      setPreview(data);
    } catch (err) {
      handleErrorCode(err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const pushUpdate = async () => {
    setPushLoading(true);
    try {
      const { data } = await axios.post(`${API}/videos/${videoId}/inject-link`, {
        cta_line: cta || DEFAULT_CTA,
        dry_run: false,
      });
      setPreview(data);
      setStatus({
        is_injected: true,
        short_url: data.short_url,
        cta_line: data.cta_line,
        injected_at: data.injected_at,
      });
      toast.success("Link injected into YouTube description.");
    } catch (err) {
      handleErrorCode(err);
    } finally {
      setPushLoading(false);
    }
  };

  const removeBlock = async () => {
    setRemoveLoading(true);
    try {
      await axios.delete(`${API}/videos/${videoId}/inject-link`);
      setStatus({ is_injected: false });
      setPreview(null);
      toast.success("Link block removed from description.");
    } catch (err) {
      handleErrorCode(err);
    } finally {
      setRemoveLoading(false);
    }
  };

  /**
   * Copy-paste fallback — used for legacy sessions that connected before we
   * upgraded to `youtube.force-ssl` write scope. Asks the backend to compose
   * the inject block (no YouTube write call) and drops it on the clipboard so
   * the user can paste it into YouTube Studio while they're nudged to reconnect.
   */
  const copyBlock = async () => {
    setPushLoading(true);
    try {
      const { data } = await axios.get(`${API}/videos/${videoId}/inject-block`, {
        params: { cta_line: cta || DEFAULT_CTA },
      });
      try {
        await navigator.clipboard.writeText(data.block_text);
      } catch {
        // Fallback for browsers that block clipboard API on iframes
        const ta = document.createElement("textarea");
        ta.value = data.block_text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setPreview({
        previous_description: "",
        new_description: data.block_text,
        short_url: data.short_url,
      });
      toast.success("Block copied! Paste it into your video description in YouTube Studio.");
    } catch (err) {
      handleErrorCode(err);
    } finally {
      setPushLoading(false);
    }
  };

  // Map backend error codes to UX
  function handleErrorCode(err) {
    const detail = err?.response?.data?.detail;
    const code = typeof detail === "object" ? detail?.code : null;
    const message = typeof detail === "object" ? detail?.message : (detail || err.message);
    if (code === "youtube_not_connected") {
      toast.error("Connect your YouTube channel first.");
      onReauthorize?.();
    } else if (code === "reauthorize_required") {
      toast.error("Reconnect YouTube to grant edit permission.");
      onReauthorize?.();
    } else if (code === "no_short_link") {
      toast.error("Click the short-link button on this row first to mint a link.");
    } else if (code === "subscription_required") {
      toast.error("Upgrade to inject links into your videos.");
    } else {
      toast.error(message || "Couldn't update description.");
    }
  }

  // Render
  if (!ytStatus) {
    return null; // parent still loading
  }

  const buttonLabel = !ytStatus.connected
    ? "Connect"
    : !ytStatus.can_write
      ? "Copy block"
      : status?.is_injected
        ? "Update"
        : "Inject";

  const buttonStyle = !ytStatus.connected
    ? "bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30"
    : status?.is_injected
      ? "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
      : "bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/25";

  const handleTrigger = (e) => {
    e?.stopPropagation();
    if (!ytStatus.connected) {
      onReauthorize?.();
      return;
    }
    setOpen(true);
  };

  return (
    <>
      <Button
        size="sm"
        onClick={handleTrigger}
        className={`flex-shrink-0 h-8 px-2.5 gap-1 text-xs ${buttonStyle}`}
        data-testid={`${testid}-trigger`}
        title={
          !ytStatus.connected
            ? "Connect YouTube to track inject status"
            : !ytStatus.can_write
              ? "Copy a ready-to-paste tracking block to your clipboard (reconnect YouTube to enable one-click push)"
              : status?.is_injected
                ? "Update the link block in this video's description"
                : "Inject the short link into this video's description"
        }
      >
        {!ytStatus.connected ? <AlertTriangle className="w-3.5 h-3.5" /> : status?.is_injected ? <Check className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
        {buttonLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border border-white/10 text-white sm:max-w-2xl" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-red-400" />
              Inject link into description
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              <span className="block truncate max-w-full" title={videoTitle}>{videoTitle}</span>
            </DialogDescription>
          </DialogHeader>

          {statusLoading ? (
            <div className="py-8 flex items-center justify-center text-zinc-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading status…
            </div>
          ) : (
            <div className="space-y-4">
              {writeFallbackMode && (
                <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs px-3 py-2.5 flex gap-2" data-testid={`${testid}-pending-banner`}>
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-400" />
                  <div>
                    <strong className="text-amber-100">Reconnect to enable one-click push.</strong> Your YouTube connection was made before we upgraded to write access. Sign out of YouTube in the sidebar and reconnect — you&rsquo;ll get a &ldquo;Manage your YouTube account&rdquo; permission — and this button will push straight to YouTube. For now, click &ldquo;Copy block&rdquo; and paste it into the video&rsquo;s description in YouTube Studio.
                  </div>
                </div>
              )}

              {status?.is_injected && (
                <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs px-3 py-2 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Currently injected — last pushed {status.injected_at ? new Date(status.injected_at).toLocaleString() : "recently"}.
                </div>
              )}

              <div>
                <label className="text-xs text-zinc-400 uppercase tracking-widest mb-2 block">Call-to-action line</label>
                <Input
                  value={cta}
                  onChange={(e) => setCta(e.target.value)}
                  placeholder={DEFAULT_CTA}
                  className="bg-black/40 border-white/10 text-white"
                  data-testid={`${testid}-cta-input`}
                  maxLength={140}
                />
                <p className="text-[11px] text-zinc-500 mt-1.5">
                  This appears above the short link in the video description. Keep it short and punchy.
                </p>
              </div>

              {preview && (
                <div className="space-y-2">
                  <label className="text-xs text-zinc-400 uppercase tracking-widest block">
                    {writeFallbackMode ? "Ready to paste — block content" : "Preview — new description"}
                  </label>
                  <Textarea
                    value={preview.new_description}
                    readOnly
                    rows={writeFallbackMode ? 7 : 10}
                    className="bg-black/40 border-white/10 text-zinc-200 font-mono text-xs"
                    data-testid={`${testid}-preview`}
                  />
                  <a
                    href={preview.short_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-zinc-500 hover:text-red-400 inline-flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" /> {preview.short_url}
                  </a>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex sm:justify-between gap-2 mt-4">
            <div className="flex gap-2">
              {status?.is_injected && (
                <Button
                  variant="outline"
                  onClick={removeBlock}
                  disabled={removeLoading || pushLoading || previewLoading}
                  className="border-red-500/30 text-red-300 hover:bg-red-500/10"
                  data-testid={`${testid}-remove`}
                >
                  {removeLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  <span className="ml-1.5">Remove from description</span>
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {!writeFallbackMode && (
                <Button
                  variant="outline"
                  onClick={runPreview}
                  disabled={previewLoading || pushLoading || removeLoading}
                  className="border-white/10 hover:bg-white/5"
                  data-testid={`${testid}-preview-btn`}
                >
                  {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                  Preview
                </Button>
              )}
              {writeFallbackMode ? (
                <Button
                  onClick={copyBlock}
                  disabled={pushLoading || previewLoading || removeLoading}
                  className="bg-red-600 hover:bg-red-500 text-white"
                  data-testid={`${testid}-copy-block`}
                >
                  {pushLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                  Copy block
                </Button>
              ) : (
                <Button
                  onClick={pushUpdate}
                  disabled={pushLoading || previewLoading || removeLoading}
                  className="bg-red-600 hover:bg-red-500 text-white"
                  data-testid={`${testid}-push`}
                >
                  {pushLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                  {status?.is_injected ? "Update on YouTube" : "Push to YouTube"}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Hook that fetches & caches the user's YT OAuth status. Reusable across rows.
 * Also exposes `reauthorize()` which opens the consent popup and re-fetches
 * status on success.
 */
export function useYouTubeStatus() {
  const [status, setStatus] = useState(null); // null | { connected, can_write, ... }
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/youtube/oauth/status`);
      setStatus(data);
    } catch {
      setStatus({ connected: false, can_write: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Listen for the postMessage emitted by /api/youtube/oauth/callback so we
  // know to refresh status after a reconnect.
  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== window.location.origin) return;
      const d = event.data;
      if (d && d.type === "vidroi-yt-oauth" && d.success) {
        refresh();
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [refresh]);

  const reauthorize = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/youtube/oauth/start`);
      if (!data?.auth_url) {
        toast.error("YouTube OAuth is not configured.");
        return;
      }
      const w = 600, h = 700;
      const left = window.screen.width / 2 - w / 2;
      const top = window.screen.height / 2 - h / 2;
      window.open(
        data.auth_url,
        "vidroi-yt-oauth",
        `width=${w},height=${h},left=${left},top=${top},popup=yes`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start the YouTube reconnect.");
    }
  }, []);

  return { status, loading, refresh, reauthorize };
}
